/**
 * Navbar Component - Jira-like global header
 */
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toggleSidebar } from '@/store/uiSlice';
import { logout } from '@/store/authSlice';
import {
  Grid,
  Search,
  Bell,
  HelpCircle,
  Settings,
  Plus,
  Bot,
  ChevronDown,
} from 'lucide-react';
import { getInitials, getRandomColor } from '@/utils/helpers';
import { useState, useEffect, useRef } from 'react';
import { AvatarTooltip } from '@/components/AvatarTooltip';
import { GlobalSearch } from '@/components/GlobalSearch';

export const Navbar = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [menuOpen, setMenuOpen] = useState(false);
  const sidebarCollapsed = useAppSelector((s) => (s as any).ui?.sidebarCollapsed) as boolean;
  const menuRef = useRef<HTMLDivElement | null>(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="px-3 sm:px-4 lg:px-6">
        <div className="h-14 flex items-center gap-3">

          <button
            className="w-8 h-8 inline-flex items-center justify-center border border-gray-300 rounded bg-white text-gray-600 hover:bg-gray-50"
            onClick={() => dispatch(toggleSidebar())}
            title={sidebarCollapsed ? 'Expand' : 'Collapse'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="8" y1="3" x2="8" y2="21" />
                <polyline points="10 8 14 12 10 16" />
              </svg>
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="8" y1="3" x2="8" y2="21" />
                <polyline points="14 8 10 12 14 16" />
              </svg>
            )}
          </button>
          {/* Left: App switcher + brand */}
          {/* <button className="p-2 rounded hover:bg-gray-100 text-gray-600" title="Apps">
            <Grid className="w-5 h-5" />
          </button> */}

          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary-600 rounded flex items-center justify-center">
              <span className="text-white font-bold text-sm">WN</span>
            </div>
            <span className="text-base font-semibold text-gray-900 hidden sm:block">WorkNex</span>
          </Link>



          {/* Middle: Global search */}
          <div className="flex-1 flex justify-center">
            <GlobalSearch />
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {/* <button
              onClick={() => navigate('/tasks')}
              className="h-9 px-3 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Create
            </button> */}

            {/* <button
              className="h-9 px-3 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 hidden md:flex items-center gap-2"
              title="AI Assistant"
            >
              <Bot className="w-4 h-4 text-purple-600" /> Ask Rovo
            </button>

            <button className="p-2 rounded hover:bg-gray-100 text-gray-600" title="Notifications">
              <Bell className="w-5 h-5" />
            </button>
            <button className="p-2 rounded hover:bg-gray-100 text-gray-600" title="Help">
              <HelpCircle className="w-5 h-5" />
            </button> */}
            <AvatarTooltip label="Settings" position="bottom">
              <button className="p-2 rounded hover:bg-gray-100 text-gray-600" onClick={() => navigate('/settings')}>
                <Settings className="w-5 h-5" />
              </button>
            </AvatarTooltip>

            {/* Auth area */}
            {isAuthenticated && user ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center gap-2 p-1 rounded hover:bg-gray-100"
                >
                  <AvatarTooltip label={user.name || user.email || 'User'} position="bottom">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${getRandomColor(
                        user.email || user.name || 'U'
                      )}`}
                    >
                      {getInitials(user.name || user.email || 'User')}
                    </div>
                  </AvatarTooltip>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg py-1">
                    <button
                      onClick={() => { setMenuOpen(false); navigate('/settings'); }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Profile & Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-gray-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="h-9 px-3 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

